import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton } from '@mui/material'
import { Fullscreen, FullscreenExit } from '@mui/icons-material'
import './DesignCanvas.css'
import * as fabric from 'fabric'
import { useLanguage } from '../../Commun/contexts/LanguageContext'
import { ComponentItem } from './ComponentLibrary'

export interface Splitter {
    id: string
    type: 'vertical' | 'horizontal'
    position: number // percentage or absolute position
}

export interface Sash {
    id: string
    x: number
    y: number
    width: number
    height: number
    type: 'fixed' | 'opening' | 'tilt' | 'casement' | 'sliding' | 'pivot' | 'accordion' | 'lift_sliding'
}

interface DesignCanvasProps {
    width: number
    height: number
    onDimensionsChange?: (width: number, height: number) => void
    splitters?: Splitter[]
    sashes?: Sash[]
    onSplittersChange?: (splitters: Splitter[]) => void
    onSashesChange?: (sashes: Sash[]) => void
    activeTool?: string
    shapeType?: number
    themeMode?: 'light' | 'dark'
    frameThickness?: number
    toolboxOverlay?: React.ReactNode
    componentLibraryOverlay?: React.ReactNode
    components?: ComponentItem[]
    surfaceMaterial?: string
}

const DesignCanvas: React.FC<DesignCanvasProps> = ({
    width,
    height,
    onDimensionsChange,
    splitters = [],
    sashes = [],
    onSplittersChange,
    onSashesChange,
    shapeType = 1, // Rectangular by default
    themeMode = 'light',
    activeTool = 'select',
    frameThickness = 2,
    toolboxOverlay,
    componentLibraryOverlay,
    components = [],
    surfaceMaterial = 'ClearGlass'
}) => {
    const { t } = useLanguage()
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const fabricCanvasRef = useRef<any>(null)
    const [scale, setScale] = useState(1)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showComponentLibrary, setShowComponentLibrary] = useState(false)
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 })

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        if (!wrapperRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                if (width > 0 && height > 0) {
                    setCanvasSize(prev => {
                        // Prevent infinite resize loops by checking threshold
                        if (Math.abs(prev.width - width) > 2 || Math.abs(prev.height - height) > 2) {
                            return { width: Math.floor(width), height: Math.floor(height) };
                        }
                        return prev;
                    });
                }
            }
        });
        observer.observe(wrapperRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Better background for technical drawing in dark mode:
    const canvasBg = '#f4f5f7'; // Force light background to match CAD style
    //const canvasBg = themeMode === 'dark' ? '#1e1e1e' : '#fcfcfc';
    // 1. Initialize Fabric Canvas once
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = new (fabric as any).Canvas(canvasRef.current, {
            selection: false,
            width: 800,
            height: 600,
        });

        fabricCanvasRef.current = canvas;

        return () => {
            canvas.dispose();
            fabricCanvasRef.current = null;
        };
    }, []);

    // 2. Handle drawing and resizing
    useEffect(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas || canvasSize.width === 0 || canvasSize.height === 0) return;

        canvas.clear();
        canvas.setDimensions({ width: canvasSize.width, height: canvasSize.height });

        // Calculate scale to fit design in canvas. We leave padding for dimensions
        const padding = 120; // Extra padding for dimension lines
        const maxWidth = canvasSize.width - padding * 2;
        const maxHeight = canvasSize.height - padding * 2;

        // Prevent negative values if canvas is too small
        if (maxWidth <= 0 || maxHeight <= 0) return;

        const scaleX = maxWidth / width
        const scaleY = maxHeight / height
        const newScale = Math.min(scaleX, scaleY, 2) // Allow scaling up for visibility
        setScale(newScale)

        const scaledWidth = width * newScale
        const scaledHeight = height * newScale
        const offsetX = (canvasSize.width - scaledWidth) / 2
        const offsetY = (canvasSize.height - scaledHeight) / 2

        // We use a fixed visual profile width for rendering so it always looks proportional
        const visualProfileW = Math.max(15, frameThickness * newScale);

        let frameColor = '#f0f0f0';
        if (themeMode === 'dark') frameColor = '#444'; // slightly lighter than canvas
        
        let glassColor = '#e1ebf4'; // default ClearGlass
        if (surfaceMaterial === 'FrostedGlass') glassColor = '#f0f4f8';
        else if (surfaceMaterial === 'TintedBronze') glassColor = '#d2b48c';
        else if (surfaceMaterial === 'TintedBlue') glassColor = '#add8e6';
        else if (surfaceMaterial === 'ResinClear') glassColor = '#f0f8ff';
        else if (surfaceMaterial === 'ResinOpaque') glassColor = '#ffffff';
        else if (surfaceMaterial === 'SolidPanel') glassColor = frameColor;

        const frameStroke = '#000000';
        const glassStroke = '#000000';

        const commonProfileProps = {
            fill: frameColor,
            stroke: frameStroke,
            strokeWidth: 1,
            selectable: true,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            hoverCursor: 'pointer',
            borderColor: '#000000',
            cornerColor: '#000000',
            borderScaleFactor: 2
        };

        const topProfile = new (fabric as any).Polygon([
            { x: offsetX, y: offsetY },
            { x: offsetX + scaledWidth, y: offsetY },
            { x: offsetX + scaledWidth - visualProfileW, y: offsetY + visualProfileW },
            { x: offsetX + visualProfileW, y: offsetY + visualProfileW }
        ], { ...commonProfileProps, id: 'profile-top' });

        const rightProfile = new (fabric as any).Polygon([
            { x: offsetX + scaledWidth, y: offsetY },
            { x: offsetX + scaledWidth, y: offsetY + scaledHeight },
            { x: offsetX + scaledWidth - visualProfileW, y: offsetY + scaledHeight - visualProfileW },
            { x: offsetX + scaledWidth - visualProfileW, y: offsetY + visualProfileW }
        ], { ...commonProfileProps, id: 'profile-right' });

        const bottomProfile = new (fabric as any).Polygon([
            { x: offsetX + scaledWidth, y: offsetY + scaledHeight },
            { x: offsetX, y: offsetY + scaledHeight },
            { x: offsetX + visualProfileW, y: offsetY + scaledHeight - visualProfileW },
            { x: offsetX + scaledWidth - visualProfileW, y: offsetY + scaledHeight - visualProfileW }
        ], { ...commonProfileProps, id: 'profile-bottom' });

        const leftProfile = new (fabric as any).Polygon([
            { x: offsetX, y: offsetY + scaledHeight },
            { x: offsetX, y: offsetY },
            { x: offsetX + visualProfileW, y: offsetY + visualProfileW },
            { x: offsetX + visualProfileW, y: offsetY + scaledHeight - visualProfileW }
        ], { ...commonProfileProps, id: 'profile-left' });

        // Inner frame (glass area)
        const innerGlass = new (fabric as any).Rect({
            left: offsetX + visualProfileW,
            top: offsetY + visualProfileW,
            width: scaledWidth - (2 * visualProfileW),
            height: scaledHeight - (2 * visualProfileW),
            fill: glassColor,
            stroke: glassStroke,
            strokeWidth: 1,
            originX: 'left',
            originY: 'top',
        });

        // Dashed lines for opening mechanism (< shape)
        const apexX = offsetX + visualProfileW;
        const apexY = offsetY + scaledHeight / 2;
        const topRX = offsetX + scaledWidth - visualProfileW;
        const topRY = offsetY + visualProfileW;
        const botRX = offsetX + scaledWidth - visualProfileW;
        const botRY = offsetY + scaledHeight - visualProfileW;

        const dashColor = '#000000';

        const dashLine1 = new (fabric as any).Line([topRX, topRY, apexX, apexY], {
            stroke: dashColor, strokeWidth: 1, strokeDashArray: [15, 15]
        });
        const dashLine2 = new (fabric as any).Line([botRX, botRY, apexX, apexY], {
            stroke: dashColor, strokeWidth: 1, strokeDashArray: [15, 15]
        });

        // Pane number "1"
        const centerText = new (fabric as any).Text("1", {
            left: offsetX + scaledWidth / 2,
            top: offsetY + scaledHeight / 2,
            fontSize: 18,
            fontWeight: 'bold',
            fill: '#000000',
            fontFamily: 'Arial, sans-serif',
            originX: 'center',
            originY: 'center',
        });

        // Handle
        const handleColor = '#ffffff';
        const handlePlate = new (fabric as any).Rect({
            left: offsetX + visualProfileW - 8, // Draw handle ON the frame edge
            top: offsetY + scaledHeight / 2 - 25,
            width: 10,
            height: 50,
            fill: handleColor,
            stroke: '#000000',
            strokeWidth: 1,
            rx: 2, ry: 2,
            originX: 'left',
            originY: 'top',
        });
        const handleBar = new (fabric as any).Rect({
            left: offsetX + visualProfileW - 4, // Draw handle bar slightly overlapping plate
            top: offsetY + scaledHeight / 2,
            width: 8,
            height: 40,
            fill: handleColor,
            stroke: '#000000',
            strokeWidth: 1,
            rx: 2, ry: 2,
            originX: 'left',
            originY: 'top',
        });

        canvas.add(topProfile, rightProfile, bottomProfile, leftProfile, innerGlass, dashLine1, dashLine2, centerText, handlePlate, handleBar);

        // --- Dimension lines (CAD style) ---
        const dimColor = '#000000';
        const drawDim = (x1: number, y1: number, x2: number, y2: number, text: string, offset: number, isVertical: boolean) => {
            const dx = isVertical ? offset : 0;
            const dy = isVertical ? 0 : offset;

            // Extension lines start EXACTLY at the object edges and extend slightly past the dimension line
            const pastAmount = 8;
            const extDx = isVertical ? (dx > 0 ? dx + pastAmount : dx - pastAmount) : 0;
            const extDy = !isVertical ? (dy > 0 ? dy + pastAmount : dy - pastAmount) : 0;

            const ext1 = new (fabric as any).Line([x1, y1, x1 + extDx, y1 + extDy], {
                stroke: dimColor, strokeWidth: 0.5, selectable: false
            });
            const ext2 = new (fabric as any).Line([x2, y2, x2 + extDx, y2 + extDy], {
                stroke: dimColor, strokeWidth: 0.5, selectable: false
            });

            // Dimension line
            const dimLine = new (fabric as any).Line([x1 + dx, y1 + dy, x2 + dx, y2 + dy], {
                stroke: dimColor, strokeWidth: 1, selectable: false
            });

            // Ticks (perpendicular lines)
            const tickSize = 6;
            const t1 = new (fabric as any).Line(
                isVertical
                    ? [x1 + dx - tickSize, y1 + dy, x1 + dx + tickSize, y1 + dy]
                    : [x1 + dx, y1 + dy - tickSize, x1 + dx, y1 + dy + tickSize],
                { stroke: dimColor, strokeWidth: 1.5, selectable: false });

            const t2 = new (fabric as any).Line(
                isVertical
                    ? [x2 + dx - tickSize, y2 + dy, x2 + dx + tickSize, y2 + dy]
                    : [x2 + dx, y2 + dy - tickSize, x2 + dx, y2 + dy + tickSize],
                { stroke: dimColor, strokeWidth: 1.5, selectable: false });

            // Text Background
            const textLeft = (x1 + x2) / 2 + dx;
            const textTop = (y1 + y2) / 2 + dy;

            const textEl = new (fabric as any).Text(text, {
                left: textLeft,
                top: textTop,
                fontSize: 14,
                fontFamily: 'Arial, sans-serif',
                fill: dimColor,
                originX: 'center',
                originY: 'center',
                angle: isVertical ? -90 : 0,
                selectable: false,
                backgroundColor: canvasBg, // opaque background
            });

            canvas.add(ext1, ext2, dimLine, t1, t2, textEl);
        }

        const tStr = frameThickness.toFixed(1);

        // Top Dim (Total Width)
        drawDim(offsetX, offsetY, offsetX + scaledWidth, offsetY, `${width.toFixed(1)}`, -30, false);
        // Left Dim (Total Height)
        drawDim(offsetX, offsetY, offsetX, offsetY + scaledHeight, `${height.toFixed(1)}`, -40, true);

        // Inner dimensions dynamically calculated based on splitters
        // --- Bottom Layout (Horizontal segments) ---
        const vSplitters = [...splitters].filter(s => s.type === 'vertical').sort((a, b) => a.position - b.position);

        let prevVisualX = offsetX + visualProfileW;
        let prevRealX = frameThickness;

        // Start Profile
        drawDim(offsetX, offsetY + scaledHeight, prevVisualX, offsetY + scaledHeight, tStr, 40, false);

        vSplitters.forEach(s => {
            const centerVisualX = offsetX + (scaledWidth * s.position / 100);
            const centerRealX = width * (s.position / 100);

            const splitVisualLeft = centerVisualX - visualProfileW / 2;
            const splitRealLeft = centerRealX - frameThickness / 2;

            const glassW = splitRealLeft - prevRealX;
            if (glassW > 0) {
                drawDim(prevVisualX, offsetY + scaledHeight, splitVisualLeft, offsetY + scaledHeight, glassW.toFixed(1), 40, false);
            }

            const splitVisualRight = centerVisualX + visualProfileW / 2;
            const splitRealRight = centerRealX + frameThickness / 2;
            drawDim(splitVisualLeft, offsetY + scaledHeight, splitVisualRight, offsetY + scaledHeight, tStr, 40, false);

            prevVisualX = splitVisualRight;
            prevRealX = splitRealRight;
        });

        const rightInnerVisualX = offsetX + scaledWidth - visualProfileW;
        const rightInnerRealX = width - frameThickness;
        const finalGlassW = rightInnerRealX - prevRealX;
        if (finalGlassW > 0) {
            drawDim(prevVisualX, offsetY + scaledHeight, rightInnerVisualX, offsetY + scaledHeight, finalGlassW.toFixed(1), 40, false);
        }

        // End Profile
        drawDim(rightInnerVisualX, offsetY + scaledHeight, offsetX + scaledWidth, offsetY + scaledHeight, tStr, 40, false);

        // --- Right Layout (Vertical segments) ---
        const hSplitters = [...splitters].filter(s => s.type === 'horizontal').sort((a, b) => a.position - b.position);

        let prevVisualY = offsetY + visualProfileW;
        let prevRealY = frameThickness;

        // Top Profile
        drawDim(offsetX + scaledWidth, offsetY, offsetX + scaledWidth, prevVisualY, tStr, 50, true);

        hSplitters.forEach(s => {
            const centerVisualY = offsetY + (scaledHeight * s.position / 100);
            const centerRealY = height * (s.position / 100);

            const splitVisualTop = centerVisualY - visualProfileW / 2;
            const splitRealTop = centerRealY - frameThickness / 2;

            const glassH = splitRealTop - prevRealY;
            if (glassH > 0) {
                drawDim(offsetX + scaledWidth, prevVisualY, offsetX + scaledWidth, splitVisualTop, glassH.toFixed(1), 50, true);
            }

            const splitVisualBot = centerVisualY + visualProfileW / 2;
            const splitRealBot = centerRealY + frameThickness / 2;
            drawDim(offsetX + scaledWidth, splitVisualTop, offsetX + scaledWidth, splitVisualBot, tStr, 50, true);

            prevVisualY = splitVisualBot;
            prevRealY = splitRealBot;
        });

        const botInnerVisualY = offsetY + scaledHeight - visualProfileW;
        const botInnerRealY = height - frameThickness;
        const finalGlassH = botInnerRealY - prevRealY;
        if (finalGlassH > 0) {
            drawDim(offsetX + scaledWidth, prevVisualY, offsetX + scaledWidth, botInnerVisualY, finalGlassH.toFixed(1), 50, true);
        }

        // Bottom Profile
        drawDim(offsetX + scaledWidth, botInnerVisualY, offsetX + scaledWidth, offsetY + scaledHeight, tStr, 50, true);

        // Draw splitters
        splitters.forEach((splitter) => {
            let rect: any
            if (splitter.type === 'vertical') {
                const x = offsetX + (scaledWidth * splitter.position / 100) - visualProfileW / 2
                rect = new (fabric as any).Rect({
                    left: x,
                    top: offsetY,
                    width: visualProfileW,
                    height: scaledHeight,
                    fill: frameColor,
                    stroke: frameStroke,
                    strokeWidth: 1,
                    selectable: true,
                    hasControls: false,
                    evented: true,
                    lockMovementY: true,
                    originX: 'left',
                    originY: 'top',
                })
            } else {
                const y = offsetY + (scaledHeight * splitter.position / 100) - visualProfileW / 2
                rect = new (fabric as any).Rect({
                    left: offsetX,
                    top: y,
                    width: scaledWidth,
                    height: visualProfileW,
                    fill: frameColor,
                    stroke: frameStroke,
                    strokeWidth: 1,
                    selectable: true,
                    hasControls: false,
                    evented: true,
                    lockMovementX: true,
                    originX: 'left',
                    originY: 'top',
                })
            }
            rect.splitterId = splitter.id

            rect.on('moving', () => {
                if (splitter.type === 'vertical') {
                    const minX = offsetX + visualProfileW;
                    const maxX = offsetX + scaledWidth - visualProfileW * 1.5;
                    if (rect.left < minX) rect.set({ left: minX });
                    if (rect.left > maxX) rect.set({ left: maxX });
                } else {
                    const minY = offsetY + visualProfileW;
                    const maxY = offsetY + scaledHeight - visualProfileW * 1.5;
                    if (rect.top < minY) rect.set({ top: minY });
                    if (rect.top > maxY) rect.set({ top: maxY });
                }
            });

            rect.on('modified', () => {
                let newPos = 0;
                if (splitter.type === 'vertical') {
                    newPos = ((rect.left - offsetX + visualProfileW / 2) / scaledWidth) * 100;
                } else {
                    newPos = ((rect.top - offsetY + visualProfileW / 2) / scaledHeight) * 100;
                }
                newPos = Math.max(0, Math.min(100, newPos));
                if (onSplittersChange) {
                    const updatedSplitters = splitters.map(s => s.id === splitter.id ? { ...s, position: newPos } : s);
                    onSplittersChange(updatedSplitters);
                }
            });

            canvas.add(rect)
        })

        // Draw sashes
        sashes.forEach((sash) => {
            const sashRect = new (fabric as any).Rect({
                left: offsetX + (sash.x * newScale),
                top: offsetY + (sash.y * newScale),
                width: sash.width * newScale,
                height: sash.height * newScale,
                fill: 'transparent',
                stroke: '#000000', // black stroke for sashes to match
                strokeWidth: 2,
                rx: 2,
                ry: 2,
                selectable: true,
                hasControls: true,
                originX: 'left',
                originY: 'top',
            })
            sashRect.sashId = sash.id
            canvas.add(sashRect)
        })

        // Draw components (Roller, Screen, Shutter)
        const hasRoller = components.some(c => c.category === 'roller');
        if (hasRoller) {
            const rollerHeight = 40 * newScale;
            const rollerBox = new (fabric as any).Rect({
                left: offsetX,
                top: offsetY - rollerHeight,
                width: scaledWidth,
                height: rollerHeight,
                fill: '#d0d0d0',
                stroke: '#000000',
                strokeWidth: 1,
                selectable: false,
                originX: 'left',
                originY: 'top',
            });
            canvas.add(rollerBox);
            for(let i=1; i<4; i++) {
                const slatLine = new (fabric as any).Line(
                    [offsetX, offsetY - rollerHeight + i * (rollerHeight / 4), offsetX + scaledWidth, offsetY - rollerHeight + i * (rollerHeight / 4)],
                    { stroke: '#aaaaaa', strokeWidth: 1, selectable: false }
                );
                canvas.add(slatLine);
            }
        }

        const hasScreen = components.some(c => c.category === 'screen');
        if (hasScreen) {
            const screenRect = new (fabric as any).Rect({
                left: offsetX + visualProfileW,
                top: offsetY + visualProfileW,
                width: scaledWidth - (2 * visualProfileW),
                height: scaledHeight - (2 * visualProfileW),
                fill: 'rgba(0,0,0,0.15)',
                stroke: '#000000',
                strokeDashArray: [2, 2],
                strokeWidth: 1,
                selectable: false,
                originX: 'left',
                originY: 'top',
            });
            canvas.add(screenRect);
        }

        const hasShutter = components.some(c => c.category === 'shutter');
        if (hasShutter) {
            const slatHeight = 10 * newScale;
            const glassTop = offsetY + visualProfileW;
            const glassHeight = scaledHeight - (2 * visualProfileW);
            const numSlats = Math.floor(glassHeight / slatHeight);
            
            for(let i=0; i<numSlats; i++) {
                const slat = new (fabric as any).Rect({
                    left: offsetX + visualProfileW,
                    top: glassTop + (i * slatHeight),
                    width: scaledWidth - (2 * visualProfileW),
                    height: slatHeight - 1,
                    fill: '#c0c0c0',
                    stroke: '#888888',
                    strokeWidth: 0.5,
                    selectable: false,
                    originX: 'left',
                    originY: 'top',
                });
                canvas.add(slat);
            }
        }



        // Handle canvas click for adding splitters
        canvas.off('mouse:down');
        canvas.on('mouse:down', (options: any) => {
            if (activeTool === 'splitter-vertical' || activeTool === 'splitter-horizontal') {
                const pointer = canvas.getScenePoint(options.e);
                const newSplitter: Splitter = {
                    id: `splitter-${Date.now()}`,
                    type: activeTool === 'splitter-vertical' ? 'vertical' : 'horizontal',
                    position: activeTool === 'splitter-vertical'
                        ? ((pointer.x - offsetX) / scaledWidth) * 100
                        : ((pointer.y - offsetY) / scaledHeight) * 100,
                }
                if (onSplittersChange) {
                    onSplittersChange([...splitters, newSplitter])
                }
            } else if (activeTool === 'sash') {
                const pointer = canvas.getScenePoint(options.e);
                const newSash: Sash = {
                    id: `sash-${Date.now()}`,
                    x: (pointer.x - offsetX) / newScale,
                    y: (pointer.y - offsetY) / newScale,
                    width: 300,
                    height: 400,
                    type: 'opening',

                }
                if (onSashesChange) {
                    onSashesChange([...sashes, newSash])
                }
            }
        })

        canvas.renderAll()
    }, [width, height, splitters, sashes, activeTool, shapeType, themeMode, canvasBg, canvasSize, components, surfaceMaterial]) // Added canvasSize dependencies

    const handleClear = () => {
        if (fabricCanvasRef.current) {
            fabricCanvasRef.current.clear()
            fabricCanvasRef.current.backgroundColor = canvasBg
            fabricCanvasRef.current.renderAll()
        }
        if (onSplittersChange) onSplittersChange([])
        if (onSashesChange) onSashesChange([])
    }

    const handleDeleteSelected = () => {
        if (!fabricCanvasRef.current) return
        const activeObject = fabricCanvasRef.current.getActiveObject()
        if (activeObject) {
            if (activeObject.splitterId && onSplittersChange) {
                onSplittersChange(splitters.filter(s => s.id !== activeObject.splitterId))
            } else if (activeObject.sashId && onSashesChange) {
                onSashesChange(sashes.filter(s => s.id !== activeObject.sashId))
            }
            fabricCanvasRef.current.remove(activeObject)
            fabricCanvasRef.current.renderAll()
        }
    }

    return (
        <Box ref={containerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper', position: 'relative' }}>
            {isFullscreen && toolboxOverlay && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 60, 
                    left: 16, 
                    width: 260, 
                    maxHeight: 'calc(100vh - 80px)', 
                    overflowY: 'auto',
                    bgcolor: 'background.paper', 
                    boxShadow: 4, 
                    borderRadius: 1, 
                    zIndex: 1000 
                }}>
                    {toolboxOverlay}
                </Box>
            )}
            {isFullscreen && showComponentLibrary && componentLibraryOverlay && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 60, 
                    right: 16, 
                    width: 400, 
                    maxHeight: 'calc(100vh - 80px)', 
                    overflowY: 'auto',
                    bgcolor: 'background.paper', 
                    boxShadow: 4, 
                    borderRadius: 1, 
                    zIndex: 1000,
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('maker.design.components')}</div>
                        <IconButton size="small" onClick={() => setShowComponentLibrary(false)}>
                            <span style={{fontSize: '0.8rem', lineHeight: 1}}>✖</span>
                        </IconButton>
                    </Box>
                    {componentLibraryOverlay}
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: isFullscreen ? 0 : 1,
                    mb: isFullscreen ? 0 : 1,
                    border: 1,
                    borderBottom: isFullscreen ? 1 : undefined,
                    borderColor: 'divider'
                }}
            >
                <div style={{ color: themeMode === 'dark' ? '#ccc' : '#666', fontSize: '13px', fontWeight: 600, marginLeft: 10 }}>
                    {t('maker.design.canvas.title')}
                </div>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {isFullscreen && componentLibraryOverlay && (
                        <Button
                            variant={showComponentLibrary ? "contained" : "outlined"}
                            size="small"
                            onClick={() => setShowComponentLibrary(!showComponentLibrary)}
                            startIcon={<span style={{fontSize: '1.2em'}}>📦</span>}
                        >
                            {t('maker.design.components')}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleDeleteSelected}
                        startIcon={<span>🗑️</span>}
                    >
                        {t('maker.design.canvas.delete')}
                    </Button>
                    <Button size="small" onClick={handleClear}>{t('maker.design.canvas.clear')}</Button>
                    <IconButton size="small" onClick={toggleFullscreen}>
                        {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>

            <Box
                ref={wrapperRef}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: canvasBg
                }}>
                <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <canvas ref={canvasRef} className="fabric-canvas" />
                    </Box>
                </Box>
                <Box
                    className="canvas-stats"
                    sx={{
                        display: 'flex',
                        gap: 2,
                        p: 1,
                        borderTop: 1,
                        borderColor: 'divider',
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        width: '100%',
                        justifyContent: 'center'
                    }}
                >
                    <span>{t('maker.design.canvas.dimensions')}: {width}mm × {height}mm</span>
                    <span>{t('maker.design.canvas.splitters')}: {splitters.length}</span>
                    <span>{t('maker.design.canvas.sashes')}: {sashes.length}</span>
                </Box>
            </Box>
        </Box>
    )
}

export default DesignCanvas
